import { useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import { axiosinstance } from '../axiosClient';
import { clearAuthFailure } from '../utils/attachAuthInterceptors';
import {
  ownerLogin,
  ownerRefreshToken,
  OwnerRefreshResponse,
} from '../api/backofficeAuth';
import { normalizeEmail } from '../utils/libs';
import {
  clearOwnerTokens,
  setOwnerAuthError,
  setOwnerTokens,
} from '../redux/backoffice/ownerAuthSlice';
import { getBackofficeCredentials } from '../services/SecureCredentials';
import type { RefreshOutcome } from '../utils/refreshManager';

const OWNER_ACCESS_TOKEN_KEY = 'owner_access_token';
const OWNER_REFRESH_TOKEN_KEY = 'owner_refresh_token';

const isNetworkError = (e: any): boolean => !e?.response;

const useBackofficeRefresh = () => {
  const dispatch = useAppDispatch();
  const owner = useAppSelector(s => s.backofficeOwnerAuth);

  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    try {
      // Storage is written on every rotation; the Redux copy can be stale
      // (render closure, or redux-persist not flushed before the app died).
      const refreshToken =
        (await AsyncStorage.getItem(OWNER_REFRESH_TOKEN_KEY)) ||
        owner?.refreshToken ||
        '';

      if (!refreshToken) {
        return { kind: 'rejected' };
      }

      let res: OwnerRefreshResponse | undefined;
      try {
        res = await ownerRefreshToken(
          axiosinstance as any,
          refreshToken,
        );
      } catch (e: any) {
        if (isNetworkError(e)) return { kind: 'unreachable' };
        // Server rejected the refresh token — fall through to the
        // stored-credentials re-login below.
      }

      if (res?.status === 200 && res?.accessToken && res?.refreshToken) {
        dispatch(
          setOwnerTokens({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
          }),
        );
        await AsyncStorage.setItem(OWNER_ACCESS_TOKEN_KEY, res.accessToken);
        await AsyncStorage.setItem(OWNER_REFRESH_TOKEN_KEY, res.refreshToken);
        clearAuthFailure('backoffice');
        return { kind: 'success', accessToken: res.accessToken };
      }

      // Fallback: Attempt re-login using stored credentials
      const creds = await getBackofficeCredentials();
      if (creds?.email && creds?.password) {
        let loginRes;
        try {
          loginRes = await ownerLogin(
            axiosinstance as any,
            normalizeEmail(creds.email),
            creds.password.trim(),
          );
        } catch (e: any) {
          if (isNetworkError(e)) return { kind: 'unreachable' };
          throw e;
        }

        if (
          loginRes?.status === 200 &&
          loginRes?.accessToken &&
          loginRes?.refreshToken
        ) {
          dispatch(
            setOwnerTokens({
              accessToken: loginRes.accessToken,
              refreshToken: loginRes.refreshToken,
            }),
          );
          await AsyncStorage.setItem(
            OWNER_ACCESS_TOKEN_KEY,
            String(loginRes.accessToken),
          );
          await AsyncStorage.setItem(
            OWNER_REFRESH_TOKEN_KEY,
            String(loginRes.refreshToken),
          );
          clearAuthFailure('backoffice');
          return { kind: 'success', accessToken: String(loginRes.accessToken) };
        }
      }

      dispatch(setOwnerAuthError('Owner refresh failed'));
      return { kind: 'rejected' };
    } catch (e: any) {
      dispatch(setOwnerAuthError(e?.message || 'Owner refresh exception'));
      if (isNetworkError(e)) return { kind: 'unreachable' };
      return { kind: 'rejected' };
    }
  }, [dispatch, owner?.refreshToken]);

  const clearOwnerSession = useCallback(async () => {
    try {
      dispatch(clearOwnerTokens());
      await AsyncStorage.multiRemove([
        OWNER_ACCESS_TOKEN_KEY,
        OWNER_REFRESH_TOKEN_KEY,
      ]);
    } catch {}
  }, [dispatch]);

  return { refresh, clearOwnerSession } as const;
};

export default useBackofficeRefresh;
